import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface User {
  userId: number | string;
  username: string;
  name: string;
  rollId: number | null;
  roleName: string;
  isActive: boolean;
  accessCode: string;
}

/**
 * Roles come from the server (tblRole), not from a list in this file.
 *
 * They used to be hardcoded here as five entries, so a role added by a
 * migration could not be assigned from this screen — VPPL and PC both existed
 * in the database and in the permission map while being invisible in the only
 * UI that grants them.
 */
interface RoleOption { id: string; name: string }

export function UsersPage() {
  const qc = useQueryClient();
  const roles = useQuery({
    queryKey: ['lookup', 'roles'],
    queryFn: () => api<RoleOption[]>('/api/lookups/roles'),
    staleTime: 5 * 60_000,
  });
  const ROLES = roles.data ?? [];
  const [editingUserId, setEditingUserId] = useState<number | string | null>(null);
  const [editForm, setEditForm] = useState({ rollId: '', isActive: true, password: '' });
  const [newUserForm, setNewUserForm] = useState({ username: '', firstName: '', lastName: '', rollId: '', password: '' });
  const [showAddUser, setShowAddUser] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api<User[]>('/api/users'),
  });

  const saveUser = useMutation({
    mutationFn: (v: { userId: number | string; data: unknown }) =>
      api(`/api/users/${v.userId}`, { method: 'PUT', body: JSON.stringify(v.data) }),
    onSuccess: () => {
      setError(null);
      setEditingUserId(null);
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const createUser = useMutation({
    mutationFn: (data: unknown) =>
      api('/api/users', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      setError(null);
      setShowAddUser(false);
      setNewUserForm({ username: '', firstName: '', lastName: '', rollId: '', password: '' });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  function startEdit(u: User) {
    setError(null);
    setEditingUserId(u.userId);
    setEditForm({
      rollId: u.rollId ? String(u.rollId) : '',
      isActive: u.isActive,
      password: u.accessCode,
    });
  }

  function handleSave(uId: number | string) {
    saveUser.mutate({
      userId: uId,
      data: {
        rollId: editForm.rollId ? Number(editForm.rollId) : null,
        isActive: editForm.isActive,
        password: editForm.password,
      },
    });
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newUserForm.username.trim() || !newUserForm.password.trim()) {
      setError('Username and Password/Code are required.');
      return;
    }
    createUser.mutate({
      ...newUserForm,
      rollId: newUserForm.rollId ? Number(newUserForm.rollId) : null,
    });
  }

  const rows = users.data ?? [];

  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold text-brand">User Management</h1>
          <p className="text-xs text-slate-500 max-w-3xl">
            Configure system accounts, assign roles, and set access codes.
            Roles are read from the database, so any role added later appears here on its own.
            VPPL is the only role that can change data; Admin and PC are read-only.
            Access codes for VPPL and Admin accounts are stored hashed and show as ********.
          </p>
        </div>
        <button
          className="btn-primary text-xs py-1.5"
          onClick={() => { setError(null); setShowAddUser(!showAddUser); }}
        >
          {showAddUser ? 'Cancel' : '+ New User'}
        </button>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {showAddUser && (
        <form onSubmit={handleCreate} className="card p-4 space-y-3 max-w-xl">
          <div className="card-header font-medium p-0 border-b-0">Add New User Account</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">Username *</label>
              <input
                className="input"
                value={newUserForm.username}
                onChange={(e) => setNewUserForm(p => ({ ...p, username: e.target.value }))}
                placeholder="e.g. palvi"
              />
            </div>
            <div>
              <label className="label">Access Code / Password *</label>
              <input
                className="input"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Plaintext for staff, strong for admin"
              />
            </div>
            <div>
              <label className="label">First Name</label>
              <input
                className="input"
                value={newUserForm.firstName}
                onChange={(e) => setNewUserForm(p => ({ ...p, firstName: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Last Name</label>
              <input
                className="input"
                value={newUserForm.lastName}
                onChange={(e) => setNewUserForm(p => ({ ...p, lastName: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Assigned Role</label>
              <select
                className="input"
                value={newUserForm.rollId}
                onChange={(e) => setNewUserForm(p => ({ ...p, rollId: e.target.value }))}
              >
                <option value="">None / Guest</option>
                {ROLES.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="submit" className="btn-primary text-xs" disabled={createUser.isPending}>
              {createUser.isPending ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      )}

      <div className="card overflow-hidden">
        <div className="card-header">Active & Inactive Accounts</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2">Username</th>
                <th className="px-3 py-2">Full Name</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Access Code</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.isLoading && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading users…</td></tr>}
              {users.isError && <tr><td colSpan={6} className="p-8 text-center text-red-600">Failed to load: {(users.error as Error).message}</td></tr>}
              {!users.isLoading && rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">No users found in database.</td></tr>
              )}
              {rows.map((u) => {
                const isEditing = editingUserId === u.userId;
                return (
                  <tr key={u.userId} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2.5 font-medium">{u.username}</td>
                    <td className="px-3 py-2.5">{u.name}</td>
                    <td className="px-3 py-2.5">
                      {isEditing ? (
                        <select
                          className="input py-0.5 text-xs h-7"
                          value={editForm.rollId}
                          onChange={(e) => setEditForm(p => ({ ...p, rollId: e.target.value }))}
                        >
                          <option value="">None / Guest</option>
                          {ROLES.map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          u.rollId === 1 ? 'bg-purple-100 text-purple-700' :
                          u.rollId === 2 ? 'bg-blue-100 text-blue-700' :
                          u.rollId === 3 ? 'bg-indigo-100 text-indigo-700' :
                          u.rollId === 4 ? 'bg-amber-100 text-amber-700' :
                          u.rollId === 5 ? 'bg-emerald-100 text-emerald-700' :
                          // VPPL is deliberately the loudest badge on the page:
                          // it is the only role that can change anything, so it
                          // should be obvious at a glance who holds it.
                          u.rollId === 6 ? 'bg-rose-100 text-rose-700' :
                          u.rollId === 7 ? 'bg-teal-100 text-teal-700' :
                          u.rollId === 8 ? 'bg-red-600 text-white' :
                          u.rollId === 10 ? 'bg-cyan-100 text-cyan-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {u.roleName}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {isEditing ? (
                        <input
                          className="input py-0.5 text-xs h-7 w-36 font-mono"
                          value={editForm.password}
                          onChange={(e) => setEditForm(p => ({ ...p, password: e.target.value }))}
                        />
                      ) : (
                        <code className="font-mono text-xs text-slate-600 bg-slate-50 px-1 py-0.5 rounded">
                          {u.accessCode}
                        </code>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {isEditing ? (
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.isActive}
                            onChange={(e) => setEditForm(p => ({ ...p, isActive: e.target.checked }))}
                          />
                          <span className="text-xs">Active</span>
                        </label>
                      ) : (
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${u.isActive ? 'bg-emerald-500' : 'bg-rose-400'}`}
                              title={u.isActive ? 'Active' : 'Inactive'} />
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            className="btn-primary text-xs py-0.5 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => handleSave(u.userId)}
                            disabled={saveUser.isPending}
                          >
                            Save
                          </button>
                          <button
                            className="btn-ghost text-xs py-0.5 px-2"
                            onClick={() => setEditingUserId(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn-ghost text-xs py-0.5 px-2"
                          onClick={() => startEdit(u)}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
